import {
  afterNextRender,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule, NgFor, NgIf } from '@angular/common';
import { GestureController, IonCol, IonIcon, IonRow } from '@ionic/angular/standalone';
import { TranslatePipe } from '@ngx-translate/core';
import { QuillViewComponent } from 'ngx-quill';
import { addIcons } from 'ionicons';
import {
  bagHandleOutline,
  chevronBackOutline,
  chevronForwardOutline,
  playSkipBackOutline,
} from 'ionicons/icons';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import lottie from 'lottie-web';
import { AlbumOverlayService } from '../../providers/album-overlay.service';
import { SvgPreloadService } from '../../providers/svg-preload.service';
import { Subscription } from 'rxjs';

/** Result payload emitted on close. */
export interface AlbumOverlayCloseResult {
  activePageIndex: number;
}

/** Pinch / wheel zoom for the album stack (horizontal carousel is off while zoomed). */
const V2_ALBUM_ZOOM_MIN = 1;
const V2_ALBUM_ZOOM_MAX = 3;
const V2_ALBUM_ZOOM_REATTACH_THRESHOLD = 1.02;

@Component({
  selector: 'app-v2-album-overlay',
  standalone: true,
  imports: [
    CommonModule,
    NgIf,
    NgFor,
    TranslatePipe,
    IonRow,
    IonCol,
    IonIcon,
    QuillViewComponent,
  ],
  templateUrl: './v2-album-overlay.component.html',
  styleUrls: ['./v2-album-overlay.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class V2AlbumOverlayComponent implements OnChanges, OnDestroy {
  @ViewChild('overlayRoot', { read: ElementRef }) overlayRootEl?: ElementRef<HTMLDivElement>;
  /** Lottie `loaderSvg` while album pages snapshot is still empty (same asset as manage / RPC overlay). */
  @ViewChild('albumLayoutLoader')
  set albumLayoutLoaderRef(ref: ElementRef<HTMLElement> | undefined) {
    this.clearAlbumLayoutLottieDefer();
    this.destroyAlbumLayoutLottie();
    if (!ref?.nativeElement || !this.showAlbumLayoutLoading) {
      return;
    }
    const el = ref.nativeElement;
    this.albumLayoutLottieDeferTimer = setTimeout(() => {
      this.albumLayoutLottieDeferTimer = null;
      if (!this.showAlbumLayoutLoading || !el.isConnected) {
        return;
      }
      const svgContent = this.svgPreloadService.getSvgContent('loaderSvg');
      if (!svgContent) {
        return;
      }
      try {
        this.albumLayoutLottieInst = lottie.loadAnimation({
          container: el,
          animationData: JSON.parse(svgContent) as object,
          renderer: 'svg',
          loop: true,
          autoplay: true,
        });
      } catch {
        /* ignore */
      }
      this.cdr.markForCheck();
    }, 50);
  }

  @ViewChild('albumCardBlock', { read: ElementRef }) albumCardBlockEl?: ElementRef<HTMLElement>;
  @ViewChild('zoomSurface', { read: ElementRef }) zoomSurfaceEl?: ElementRef<HTMLElement>;
  @ViewChild('scrollCenter', { read: ElementRef }) scrollCenterEl?: ElementRef<HTMLElement>;
  @ViewChild('carouselViewport', { read: ElementRef }) viewportEl?: ElementRef<HTMLElement>;
  @ViewChild('carouselTrack', { read: ElementRef }) trackEl?: ElementRef<HTMLElement>;
  @ViewChildren('carouselSlideInner', { read: ElementRef }) slideInnerEls?: QueryList<ElementRef<HTMLElement>>;
  @ViewChildren('navSeg', { read: ElementRef }) navSegEls?: QueryList<ElementRef<HTMLElement>>;

  @Input() isOpen = false;

  /* ——— Snapshot inputs from items page ——— */
  @Input() albumPagesSorted: any[] = [];
  @Input() albumSections: any[][] = [];
  @Input() tradesEnabled = false;
  /** Unused for sticker `src`; kept for API compatibility with album overlay service. */
  @Input() stickerUrlPrefix = '';
  @Input() iconLocationPrefix = '';
  /** Same as user-collection-items grid when `item` has no `icon_path`. */
  @Input() noStickerImgP = '';
  @Input() noStickerImgL = '';
  /** Items the user owns (`Tenho`); same as album overlay service / grid. */
  @Input() ownedItemIds: Set<string> = new Set();
  @Input() fullItemList: { item_id: string; Tenho: any; icon_path?: string; picture_path?: string }[] = [];
  @Input() focusItemId?: string;
  @Input() initialPageIndex = 0;
  /** Show the order-album CTA that animates from behind the bottom nav. */
  @Input() showBuyAlbumButton = false;
  /** When true (e.g. `percent_complete >= 100`), buy CTA plays a colorful entry ring. */
  @Input() collectionComplete = false;
  /**
   * Optional element to return focus to when the overlay closes (e.g. the button that
   * opened it). The parent is responsible for passing `document.activeElement` before
   * setting `isOpen = true`. If omitted, focus is not explicitly restored.
   */
  @Input() restoreFocusEl?: HTMLElement;

  @Output() closed = new EventEmitter<AlbumOverlayCloseResult>();

  /* ——— Overlay open/close state ——— */
  domOpen = false;
  isClosing = false;
  private closeAnimationFinishTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly OVERLAY_CLOSE_ANIM_MS = 400;
  /** Delay before the buy CTA slides into view (after overlay open). */
  private static readonly BUY_ALBUM_PEEK_DELAY_MS = 900;
  private buyAlbumPeekTimer: ReturnType<typeof setTimeout> | null = null;
  /** When true, the buy strip is visible (slides up from behind `.v2-ao-nav`). */
  buyAlbumPeekVisible = false;

  /* ——— Album page state ——— */
  activePage: any = {};
  activePageIndex = 0;

  /* ——— Draggable page layout (album frame size for compute) ——— */
  width: number | null = null;
  height: number | null = null;

  /** Indices `0..totalPages-1` for nav segments (stable array, rebuilt when album opens). */
  pageIndexList: number[] = [];

  /**
   * Album page indices currently mounted in the DOM: sliding window
   * `activePageIndex ± NEIGHBOR_PAGE_RADIUS` (clamped to album bounds).
   */
  uiAlbumPageIndices: number[] = [];

  /** Per-page scaled geometry for DRAGGABLE layers in the UI window (key = album page index). */
  draggableLayoutByPageIndex: Record<number, any> = {};

  /** Neighbor radius: DOM window + prefetch span. */
  private static readonly NEIGHBOR_PAGE_RADIUS = 8;
  /** Partner draggable page size (px); must stay aligned with {@link buildDraggableLayout}. */
  private static readonly PARTNER_DRAGGABLE_PAGE_WIDTH = 793.69;
  private static readonly PARTNER_DRAGGABLE_PAGE_HEIGHT = 1122.5;

  /** URLs already requested via Image() prefetch (per overlay session). */
  private readonly prefetchedImageUrls = new Set<string>();

  /**
   * Covers the active slide and up to two neighbors on each side until every
   * participating `<img>` on those mounted slides has finished loading (or failed).
   * Shown on open and on instant far dot jumps.
   */
  albumSkeletonVisible = false;

  /** How many pages left/right of `activePageIndex` also show the skeleton overlay. */
  private static readonly ALBUM_SKELETON_NEIGHBOR_RADIUS = 2;

  /** Text announced to assistive technologies on each page commit (assertive live region). */
  pageAnnouncement = '';

  /**
   * True after {@link initAlbumState} successfully built carousel state for the current
   * open cycle. Cleared on close; used so a later `albumPagesSorted` input patch can mount
   * the album when pages arrive after an empty open.
   */
  private albumContentMounted = false;

  /** `removeEventListener` callbacks for the active-page image watch. */
  private activePageImageWatchCleanup: Array<() => void> = [];

  /* ——— ResizeObserver (remeasures slide width on viewport resize / rotation) ——— */
  private resizeObserver: ResizeObserver | null = null;
  private resizeDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * When the active slide has zero `<img>` nodes, Angular may not have projected
   * the template yet — retry a few frames before clearing the skeleton.
   */
  private albumImageWatchDomRetryCount = 0;
  private static readonly ALBUM_IMAGE_WATCH_DOM_RETRY_MAX = 8;

  /* ——— Carousel ——— */
  /** Prevents new gestures / nav while a snap animation is running. */
  isSnapping = false;

  private static readonly SLIDE_GAP_PX = 12;
  private static readonly SNAP_DURATION_MS = 330;
  private static readonly SNAP_EASING = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)';
  private static readonly ALBUM_ZOOM_RESET_MS = 340;
  /** Minimum velocity (px/ms) to commit a page change regardless of distance. */
  private static readonly VELOCITY_THRESHOLD = 0.3;
  /** Fraction of slide width needed to commit when velocity is below threshold. */
  private static readonly DISTANCE_THRESHOLD_RATIO = 0.22;

  private slideWidthPx = 0;
  private snapTimer: ReturnType<typeof setTimeout> | null = null;
  private carouselGesture: any = null;

  /** 1 = normal; two-finger pinch or Ctrl/Cmd+wheel on the scroll area. */
  albumZoomScale = 1;
  private albumPanX = 0;
  private albumPanY = 0;
  private pinchActive = false;
  private pinchStartDist = 0;
  private pinchStartScale = 1;
  private panActive = false;
  private panStartX = 0;
  private panStartY = 0;
  private panOriginX = 0;
  private panOriginY = 0;
  private albumZoomTouchCleanup: (() => void) | null = null;
  /** Ease-out after fingers lift while zoomed; keeps transform visible below zoom threshold. */
  private albumZoomEaseOutActive = false;
  private albumZoomResetRaf: number = 0;

  private readonly injector = inject(Injector);

  /** `item_id` → trimmed `icon_path` for locked preview (catalog art). */
  private fullItemIconPathById = new Map<string, string>();

  /** `item_id` → `picture_path` when API sends it (e.g. direct-upload final art). */
  private fullItemPicturePathById = new Map<string, string>();

  /** `item_id` → intrinsic pixel size for direct-upload stickers (resize-safe layout rebuilds). */
  private insertedStickerNaturalByItemId = new Map<string, { nw: number; nh: number }>();

  private albumLayoutLottieDeferTimer: ReturnType<typeof setTimeout> | null = null;
  private albumLayoutLottieInst: ReturnType<typeof lottie.loadAnimation> | null = null;
  private readonly hardwareBackSub: Subscription;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly gestureCtrl: GestureController,
    private readonly albumOverlayService: AlbumOverlayService,
    private readonly svgPreloadService: SvgPreloadService,
  ) {
    addIcons({
      bagHandleOutline,
      chevronBackOutline,
      chevronForwardOutline,
      playSkipBackOutline,
    });
    this.hardwareBackSub = this.albumOverlayService.hardwareBack$.subscribe(() => {
      if (this.isOpen && this.domOpen && !this.isClosing) {
        this.close();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fullItemList']) {
      this.rebuildStickerIconPathMap();
    }
    if (changes['albumPagesSorted'] && this.domOpen && this.isOpen && !changes['isOpen']) {
      if (!this.albumContentMounted && (this.albumPagesSorted?.length ?? 0) > 0) {
        this.initAlbumState();
        this.cdr.markForCheck();
      }
    }
    if (changes['isOpen']) {
      if (this.isOpen) {
        this.clearCloseAnimationFinishTimer();
        const wasClosing = this.isClosing;
        this.isClosing = false;
        this.domOpen = true;
        this.setBodyScrollLock(true);
        if (wasClosing) {
          setTimeout(() => this.resetOverlayEnterAnimationStyles(), 0);
        }
        this.initAlbumState();
      } else {
        this.albumContentMounted = false;
        this.clearBuyAlbumPeekTimer();
        this.buyAlbumPeekVisible = false;
        this.restoreFocusEl?.focus();
        this.prefetchedImageUrls.clear();
        this.insertedStickerNaturalByItemId.clear();
        this.teardownActivePageImageWatch();
        this.destroyGesture();
        this.cancelAlbumZoomResetAnimation();
        this.detachAlbumZoomListeners();
        this.albumZoomScale = V2_ALBUM_ZOOM_MIN;
        this.albumPanX = 0;
        this.albumPanY = 0;
        this.pinchActive = false;
        this.panActive = false;
        this.clearSnapTimer();
        this.detachResizeObserver();
        if (this.domOpen) {
          this.isClosing = true;
          this.scheduleCloseAnimationFinish();
        }
      }
      this.cdr.markForCheck();
    }

    /**
     * A second `AlbumOverlayService.open({ focusItemId, … })` while the overlay is
     * already up leaves `isOpen` as true → true, so the branch above does not run and
     * `initAlbumState()` never re-applies focus. Re-navigate when only focus (or
     * initial page) inputs change and album content is already mounted.
     */
    if (
      this.domOpen &&
      this.isOpen &&
      this.albumContentMounted &&
      !changes['isOpen'] &&
      (this.albumPagesSorted?.length ?? 0) > 0
    ) {
      if (changes['focusItemId'] && this.focusItemId) {
        const idx = this.findPageIndexForItem(String(this.focusItemId));
        if (idx >= 0 && idx !== this.activePageIndex) {
          this.goToPage(idx);
        }
        this.cdr.markForCheck();
      } else if (changes['initialPageIndex'] && this.focusItemId == null) {
        const n = this.albumPagesSorted.length;
        const idx = Math.max(0, Math.min(this.initialPageIndex ?? 0, n - 1));
        if (idx !== this.activePageIndex) {
          this.goToPage(idx);
        }
        this.cdr.markForCheck();
      }
    }
  }

  ngOnDestroy(): void {
    this.hardwareBackSub.unsubscribe();
    this.clearBuyAlbumPeekTimer();
    this.clearCloseAnimationFinishTimer();
    this.clearAlbumLayoutLottieDefer();
    this.destroyAlbumLayoutLottie();
    this.clearSnapTimer();
    this.cancelAlbumZoomResetAnimation();
    this.teardownActivePageImageWatch();
    this.destroyGesture();
    this.detachAlbumZoomListeners();
    this.detachResizeObserver();
    this.prefetchedImageUrls.clear();
    this.setBodyScrollLock(false);
  }

  private clearAlbumLayoutLottieDefer(): void {
    if (this.albumLayoutLottieDeferTimer) {
      clearTimeout(this.albumLayoutLottieDeferTimer);
      this.albumLayoutLottieDeferTimer = null;
    }
  }

  private destroyAlbumLayoutLottie(): void {
    if (this.albumLayoutLottieInst) {
      try {
        this.albumLayoutLottieInst.destroy();
      } catch {
        /* ignore */
      }
      this.albumLayoutLottieInst = null;
    }
  }

  /* ——— Keyboard navigation (desktop / PWA) ——— */

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!this.domOpen || this.isClosing) return;
    const t = event.target as HTMLElement | null;
    if (t?.closest('input, textarea, select, [contenteditable="true"]')) return;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.prevPage();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.nextPage();
        break;
      case 'Home':
        event.preventDefault();
        this.goToPage(0);
        break;
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
      case '0':
        if (this.albumAreaZoomed) {
          event.preventDefault();
          this.resetAlbumZoom();
        }
        break;
      case 'Tab':
        this.handleTabFocusTrap(event);
        break;
    }
  }

  private handleTabFocusTrap(event: KeyboardEvent): void {
    const overlay = this.overlayRootEl?.nativeElement;
    if (!overlay) return;
    const focusable = Array.from(
      overlay.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [tabindex]:not([tabindex="-1"]), a[href], input, select, textarea'
      )
    ).filter(el => !el.closest('[aria-hidden="true"]') && el.offsetParent !== null);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey) {
      if (document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  /* ——— Public API ——— */

  close(): void {
    this.emitClosed();
  }

  /** Sticker tap → parent opens v2-detail-overlay (stacked above album via z-index). */
  /** Order physical album — parent opens `RequestPhysicalCollectionOverlayService`. */
  onBuyAlbumClick(): void {
    void Haptics.impact({ style: ImpactStyle.Light });
    this.albumOverlayService.callbacks?.onBuyAlbum?.();
  }

  onAlbumStickerClick(rawId: string | number | undefined | null, ev: Event): void {
    ev.stopPropagation();
    if (ev.type === 'keydown') {
      (ev as KeyboardEvent).preventDefault();
    }
    if (rawId == null || rawId === '') {
      return;
    }
    this.albumOverlayService.callbacks?.onStickerClick?.(String(rawId));
  }

  /** Page count for nav (sections and pages should match; prefer sections when non-empty). */
  get totalPages(): number {
    const s = this.albumSections?.length ?? 0;
    const p = this.albumPagesSorted?.length ?? 0;
    return s > 0 ? s : p;
  }

  trackNavByIndex(_: number, i: number): number {
    return i;
  }

  trackUiPageByIndex(_: number, pageIdx: number): number {
    return pageIdx;
  }

  /** Album pages not yet available (parent still loading or empty snapshot on open). */
  get showAlbumLayoutLoading(): boolean {
    return this.domOpen && !this.isClosing && (this.albumPagesSorted?.length ?? 0) === 0;
  }

  getPageDef(pageIdx: number): any {
    return this.albumPagesSorted[pageIdx]?.pageDef ?? {};
  }

  pageName(pageIdx: number): string {
    return this.getPageDef(pageIdx)?.page?.name ?? '';
  }

  private teardownActivePageImageWatch(): void {
    for (const fn of this.activePageImageWatchCleanup) {
      try {
        fn();
      } catch {
        /* ignore */
      }
    }
    this.activePageImageWatchCleanup = [];
  }

  /** True when this `<img>` should participate in the “all images ready” gate. */
  private imgElementCountsForAlbumSkeleton(img: HTMLImageElement): boolean {
    const fromAttr = (img.getAttribute('src') ?? '').trim();
    if (fromAttr.length > 0) return true;
    const s = (img.currentSrc || img.src || '').trim();
    if (s.length === 0) return false;
    if (typeof window !== 'undefined' && window.location?.href && s === window.location.href) {
      return false;
    }
    return true;
  }

  /** Whether this page index should show the album skeleton (center ± neighbor radius). */
  slideShowsAlbumSkeleton(pageIdx: number): boolean {
    if (!this.albumSkeletonVisible) return false;
    return (
      Math.abs(pageIdx - this.activePageIndex) <=
      V2AlbumOverlayComponent.ALBUM_SKELETON_NEIGHBOR_RADIUS
    );
  }

  private getAlbumSkeletonWatchGlobalIndices(): number[] {
    const n = this.albumPagesSorted?.length ?? 0;
    if (n === 0) return [];
    const a = this.activePageIndex;
    const r = V2AlbumOverlayComponent.ALBUM_SKELETON_NEIGHBOR_RADIUS;
    const out: number[] = [];
    for (let d = -r; d <= r; d++) {
      const g = a + d;
      if (g >= 0 && g < n) out.push(g);
    }
    return out;
  }

  /**
   * While `albumSkeletonVisible`, attach one-shot listeners to every still-loading `<img>`
   * under mounted slides in the skeleton window (active ± radius). Hides overlays when all
   * have settled (`complete` or `error`).
   */
  private syncAlbumSkeletonWithActivePageImages(): void {
    if (!this.albumSkeletonVisible) {
      this.teardownActivePageImageWatch();
      return;
    }

    this.teardownActivePageImageWatch();

    const activeLi = this.getLocalIndex(this.activePageIndex);
    if (activeLi < 0) {
      this.albumImageWatchDomRetryCount = 0;
      this.albumSkeletonVisible = false;
      this.cdr.markForCheck();
      return;
    }

    const inners = this.slideInnerEls?.toArray();
    const activeSlideRoot = inners?.[activeLi]?.nativeElement as HTMLElement | undefined;
    if (!activeSlideRoot) {
      this.albumImageWatchDomRetryCount = 0;
      this.albumSkeletonVisible = false;
      this.cdr.markForCheck();
      return;
    }

    const watchGlobals = this.getAlbumSkeletonWatchGlobalIndices();
    const seen = new Set<HTMLImageElement>();
    const relevant: HTMLImageElement[] = [];

    for (const globalIdx of watchGlobals) {
      const li = this.getLocalIndex(globalIdx);
      if (li < 0 || !inners?.[li]) continue;
      const slideRoot = inners[li].nativeElement as HTMLElement;
      const imgs = Array.from(slideRoot.querySelectorAll('img')) as HTMLImageElement[];
      for (const el of imgs) {
        if (!this.imgElementCountsForAlbumSkeleton(el)) continue;
        if (seen.has(el)) continue;
        seen.add(el);
        relevant.push(el);
      }
    }

    if (relevant.length === 0) {
      const activeImgs = Array.from(activeSlideRoot.querySelectorAll('img')) as HTMLImageElement[];
      if (
        activeImgs.length === 0 &&
        this.albumImageWatchDomRetryCount < V2AlbumOverlayComponent.ALBUM_IMAGE_WATCH_DOM_RETRY_MAX
      ) {
        this.albumImageWatchDomRetryCount++;
        afterNextRender(() => {
          if (this.albumSkeletonVisible) {
            this.syncAlbumSkeletonWithActivePageImages();
          }
        }, { injector: this.injector });
        return;
      }
      this.albumImageWatchDomRetryCount = 0;
      this.albumSkeletonVisible = false;
      this.cdr.markForCheck();
      return;
    }

    this.albumImageWatchDomRetryCount = 0;

    let pending = 0;
    const finishIfDone = (): void => {
      if (!this.albumSkeletonVisible) return;
      this.albumSkeletonVisible = false;
      this.teardownActivePageImageWatch();
      this.cdr.markForCheck();
    };

    const onOneSettled = (): void => {
      pending--;
      if (pending <= 0) {
        finishIfDone();
      }
    };

    for (const img of relevant) {
      if (img.complete) {
        continue;
      }
      pending++;
      const onSettled = (): void => {
        img.removeEventListener('load', onSettled);
        img.removeEventListener('error', onSettled);
        onOneSettled();
      };
      img.addEventListener('load', onSettled);
      img.addEventListener('error', onSettled);
      this.activePageImageWatchCleanup.push(() => {
        img.removeEventListener('load', onSettled);
        img.removeEventListener('error', onSettled);
      });
    }

    if (pending <= 0) {
      finishIfDone();
    }
  }

  /**
   * Normalizes `pageDef.background` for `<img [src]>`.
   */
  albumBackgroundImageSrc(bg: unknown): string {
    if (bg == null) return '';
    const s = String(bg).trim();
    if (!s) return '';
    return s.replace(/^url\((['"]?)(.*)\1\)$/i, '$2').trim();
  }

  /** DRAGGABLE page background (same normalization, no `pageDef` mutation). */
  draggableBackgroundSrc(pageDef: any): string {
    return this.albumBackgroundImageSrc(pageDef?.background);
  }

  getDraggableLayout(pageIdx: number): any {
    return this.draggableLayoutByPageIndex[pageIdx];
  }

  /* ——— Carousel: per-slide visual state (used by Angular bindings in static state) ——— */

  getSlideTransform(pageIdx: number): string {
    return `scale(${this.computeSlideScale(Math.abs(pageIdx - this.activePageIndex))})`;
  }

  getSlideOpacity(pageIdx: number): number {
    return this.computeSlideOpacity(Math.abs(pageIdx - this.activePageIndex));
  }

  getSlideZIndex(pageIdx: number): number {
    const dist = Math.abs(pageIdx - this.activePageIndex);
    if (dist === 0) return 3;
    if (dist === 1) return 2;
    return 1;
  }

  private computeSlideScale(dist: number): number {
    if (dist === 0) return 1;
    if (dist === 1) return 0.88;
    if (dist === 2) return 0.78;
    return 0.70;
  }

  private computeSlideOpacity(dist: number): number {
    if (dist === 0) return 1;
    if (dist === 1) return 0.85;
    if (dist === 2) return 0.70;
    return 0.55;
  }

  /* ——— Carousel: position math ——— */

  private measureSlideWidth(): void {
    const w = this.viewportEl?.nativeElement?.clientWidth ?? 0;
    if (w > 0) this.slideWidthPx = w;
  }

  private getLocalIndex(globalIdx: number): number {
    return this.uiAlbumPageIndices.indexOf(globalIdx);
  }

  private getSlotWidth(): number {
    return this.slideWidthPx + V2AlbumOverlayComponent.SLIDE_GAP_PX;
  }

  /** Returns the translateX for the track that places `localIdx` at the viewport's left edge. */
  private computeBaseTrackX(localIdx?: number): number {
    const li = localIdx ?? this.getLocalIndex(this.activePageIndex);
    if (li < 0) return 0;
    return -li * this.getSlotWidth();
  }

  private setTrackTransform(x: number, animate: boolean): void {
    const track = this.trackEl?.nativeElement;
    if (!track) return;
    if (animate) {
      const dur = V2AlbumOverlayComponent.SNAP_DURATION_MS;
      track.style.transition = `transform ${dur}ms ${V2AlbumOverlayComponent.SNAP_EASING}`;
    } else {
      track.style.transition = '';
    }
    track.style.transform = `translate3d(${x}px,0,0)`;
  }

  /* ——— Carousel: live drag DOM updates (bypasses Angular CD for 60fps) ——— */

  private updateDragDOM(rawDeltaX: number): void {
    if (this.slideWidthPx === 0) return;

    const n = this.albumPagesSorted.length;
    const atStart = this.activePageIndex === 0 && rawDeltaX > 0;
    const atEnd = this.activePageIndex === n - 1 && rawDeltaX < 0;
    const effectiveDelta = (atStart || atEnd) ? rawDeltaX * 0.3 : rawDeltaX;

    const track = this.trackEl?.nativeElement;
    if (track) {
      track.style.transform = `translate3d(${this.computeBaseTrackX() + effectiveDelta}px,0,0)`;
    }

    // Interpolate scale/opacity on the two relevant neighbors for continuous feel
    const progress = Math.min(1, Math.abs(effectiveDelta) / this.slideWidthPx);
    const isGoingPrev = rawDeltaX > 0;
    const slides = this.slideInnerEls?.toArray() ?? [];

    slides.forEach((elRef, localIdx) => {
      const globalIdx = this.uiAlbumPageIndices[localIdx];
      const signedDist = globalIdx - this.activePageIndex;
      const absDist = Math.abs(signedDist);
      let scale: number;
      let opacity: number;

      if (absDist === 0) {
        // Active card shrinks slightly as it moves out
        scale = 1 - progress * 0.12;
        opacity = 1;
      } else if (absDist === 1) {
        // Incoming neighbor grows into view, outgoing stays put
        const isIncoming = isGoingPrev ? signedDist === -1 : signedDist === 1;
        if (isIncoming) {
          scale = 0.88 + progress * 0.12;
          opacity = 0.85 + progress * 0.15;
        } else {
          scale = 0.88;
          opacity = 0.85;
        }
      } else {
        scale = this.computeSlideScale(absDist);
        opacity = this.computeSlideOpacity(absDist);
      }

      const el = elRef.nativeElement;
      el.style.transform = `scale(${scale})`;
      el.style.opacity = String(opacity);
    });
  }

  /* ——— Carousel: snap / commit ——— */

  /**
   * Animates the track to `targetIdx` (must be in current window), then commits
   * the page change after the animation completes.
   */
  private snapToPage(targetIdx: number): void {
    if (this.isSnapping) return;
    this.isSnapping = true;
    this.clearSnapTimer();

    const localTargetIdx = this.getLocalIndex(targetIdx);
    const dur = V2AlbumOverlayComponent.SNAP_DURATION_MS;

    if (localTargetIdx >= 0 && this.slideWidthPx > 0) {
      this.setTrackTransform(this.computeBaseTrackX(localTargetIdx), true);
      // Enable CSS transitions on slide inners so scale also animates during snap
      this.slideInnerEls?.forEach(el => {
        el.nativeElement.style.transition = `transform ${dur}ms ease, opacity ${dur}ms ease`;
      });
      this.snapTimer = setTimeout(() => {
        this.snapTimer = null;
        this.commitToIndex(targetIdx, { showSkeleton: false });
      }, dur);
    } else {
      // Target not in current window (e.g. dot tapped far away) — instant
      this.commitToIndex(targetIdx, { showSkeleton: false });
    }
  }

  /** Animates the track back to resting position without changing page. */
  private snapBack(): void {
    this.clearSnapTimer();
    const dur = V2AlbumOverlayComponent.SNAP_DURATION_MS;

    this.setTrackTransform(this.computeBaseTrackX(), true);

    // Enable transitions on inners, then let Angular bindings restore correct values
    this.slideInnerEls?.forEach(el => {
      el.nativeElement.style.transition = `transform ${dur}ms ease, opacity ${dur}ms ease`;
    });
    // markForCheck causes Angular to write the correct scale/opacity via bindings;
    // the CSS transition fires on the binding change, animating back to resting values.
    this.cdr.markForCheck();

    this.snapTimer = setTimeout(() => {
      this.snapTimer = null;
      this.isSnapping = false;
      this.clearSlideInnerTransitionOnly();
      this.viewportEl?.nativeElement.classList.remove('is-dragging');
    }, dur + 20);
  }

  /**
   * Updates `activePageIndex`, rebuilds the window, repositions the track without
   * animation (the animation was already done during snap), and clears drag state.
   *
   * Track repositioning runs inside `afterNextRender` so it fires AFTER Angular has
   * written all `*ngFor` / binding changes to the DOM. This ensures the flex order
   * of slides is stable before we measure and set `translate3d`, eliminating the
   * one-frame jump that occurred with the previous double-rAF approach.
   *
   * `clearSlideInnerTransitionOnly` runs in the same callback to remove the temporary
   * snap `transition` we set on slide inners. Do **not** clear `transform`/`opacity`:
   * those come from `[style.transform]` / `[style.opacity]` and clearing them here
   * would wipe neighbor scales right after CD (they would look full-size until the next tick).
   *
   * @param showSkeleton When true (instant far `goToPage`), show skeletons on the active
   *   slide and ±2 neighbors until their images load. Omit or false after swipe snap.
   */
  private commitToIndex(globalIdx: number, opts?: { showSkeleton?: boolean }): void {
    if (opts?.showSkeleton) {
      this.albumSkeletonVisible = true;
      this.albumImageWatchDomRetryCount = 0;
    }
    this.activePageIndex = globalIdx;
    this.activePage = this.albumPagesSorted[globalIdx]?.pageDef ?? {};
    this.syncAlbumPageLayers();
    this.prepareMountedCardPages();

    // Announce new page to screen readers and fire haptic
    this.pageAnnouncement = `${globalIdx + 1} / ${this.totalPages}`;
    void this.triggerPageCommitHaptic();

    this.viewportEl?.nativeElement.classList.remove('is-dragging');
    this.isSnapping = false;

    this.cdr.markForCheck();
    this.scrollActiveNavIntoView();
    this.schedulePrefetchAlbumWindow(globalIdx);

    afterNextRender(() => {
      this.measureSlideWidth();
      this.setTrackTransform(this.computeBaseTrackX(), false);
      this.clearSlideInnerTransitionOnly();
      this.syncAlbumSkeletonWithActivePageImages();
    }, { injector: this.injector });
  }

  /**
   * Removes only the inline `transition` added for snap animations.
   * `transform` / `opacity` are owned by template bindings — clearing them after
   * `markForCheck` + CD would strip neighbor `scale(...)` until the next detection cycle.
   */
  private clearSlideInnerTransitionOnly(): void {
    this.slideInnerEls?.forEach(el => {
      el.nativeElement.style.transition = '';
    });
  }

  /* ——— Haptics ——— */

  private async triggerPageCommitHaptic(): Promise<void> {
    if (
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }
    await Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
  }

  /* ——— ResizeObserver (remeasures after rotation / iOS toolbar show/hide) ——— */

  private attachResizeObserver(): void {
    this.detachResizeObserver();
    const el = this.viewportEl?.nativeElement;
    if (!el || typeof ResizeObserver === 'undefined') return;
    this.resizeObserver = new ResizeObserver(() => {
      if (this.resizeDebounceTimer) clearTimeout(this.resizeDebounceTimer);
      this.resizeDebounceTimer = setTimeout(() => {
        this.resizeDebounceTimer = null;
        this.measureSlideWidth();
        this.setTrackTransform(this.computeBaseTrackX(), false);
      }, 80);
    });
    this.resizeObserver.observe(el);
  }

  private detachResizeObserver(): void {
    if (this.resizeDebounceTimer) {
      clearTimeout(this.resizeDebounceTimer);
      this.resizeDebounceTimer = null;
    }
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }

  /* ——— Album area pinch / wheel zoom ——— */

  private prefersReducedMotionAlbum(): boolean {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return false;
    }
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /** True when album is zoomed or easing back to 1× (carousel off). */
  get albumAreaZoomed(): boolean {
    return (
      !this.prefersReducedMotionAlbum() &&
      (this.albumZoomEaseOutActive ||
        this.albumZoomScale >= V2_ALBUM_ZOOM_REATTACH_THRESHOLD)
    );
  }

  getAlbumZoomTransform(): string {
    if (this.prefersReducedMotionAlbum()) {
      return 'none';
    }
    if (
      !this.albumZoomEaseOutActive &&
      this.albumZoomScale < V2_ALBUM_ZOOM_REATTACH_THRESHOLD
    ) {
      return 'none';
    }
    return `translate3d(${this.albumPanX}px, ${this.albumPanY}px, 0) scale(${this.albumZoomScale})`;
  }

  resetAlbumZoom(): void {
    this.pinchActive = false;
    this.panActive = false;
    this.animateAlbumZoomToNormal();
    void Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
  }

  private cancelAlbumZoomResetAnimation(): void {
    if (this.albumZoomResetRaf !== 0) {
      cancelAnimationFrame(this.albumZoomResetRaf);
      this.albumZoomResetRaf = 0;
    }
    this.albumZoomEaseOutActive = false;
  }

  /** After fingers lift while zoomed — ease scale/pan back to 1×, then re-enable carousel. */
  private animateAlbumZoomToNormal(): void {
    this.cancelAlbumZoomResetAnimation();
    if (this.prefersReducedMotionAlbum()) {
      this.albumZoomScale = V2_ALBUM_ZOOM_MIN;
      this.albumPanX = 0;
      this.albumPanY = 0;
      this.syncAlbumZoomCarouselGate({ resyncTrack: true });
      this.cdr.markForCheck();
      return;
    }
    if (
      this.albumZoomScale < V2_ALBUM_ZOOM_REATTACH_THRESHOLD &&
      Math.abs(this.albumPanX) < 0.5 &&
      Math.abs(this.albumPanY) < 0.5
    ) {
      this.albumZoomScale = V2_ALBUM_ZOOM_MIN;
      this.albumPanX = 0;
      this.albumPanY = 0;
      this.syncAlbumZoomCarouselGate({ resyncTrack: true });
      this.cdr.markForCheck();
      return;
    }

    this.albumZoomEaseOutActive = true;
    const startScale = this.albumZoomScale;
    const startX = this.albumPanX;
    const startY = this.albumPanY;
    const duration = V2AlbumOverlayComponent.ALBUM_ZOOM_RESET_MS;
    const t0 = performance.now();
    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

    const tick = (now: number): void => {
      if (!this.albumZoomEaseOutActive) {
        return;
      }
      const u = Math.min(1, (now - t0) / duration);
      const e = easeOutCubic(u);
      this.albumZoomScale =
        V2_ALBUM_ZOOM_MIN + (startScale - V2_ALBUM_ZOOM_MIN) * (1 - e);
      this.albumPanX = startX * (1 - e);
      this.albumPanY = startY * (1 - e);
      this.cdr.markForCheck();
      if (u < 1) {
        this.albumZoomResetRaf = requestAnimationFrame(tick);
      } else {
        this.albumZoomResetRaf = 0;
        this.albumZoomEaseOutActive = false;
        this.albumZoomScale = V2_ALBUM_ZOOM_MIN;
        this.albumPanX = 0;
        this.albumPanY = 0;
        this.syncAlbumZoomCarouselGate({ resyncTrack: true });
        this.cdr.markForCheck();
      }
    };
    this.albumZoomResetRaf = requestAnimationFrame(tick);
  }

  private albumTouchDistance(a: Touch, b: Touch): number {
    const dx = a.clientX - b.clientX;
    const dy = a.clientY - b.clientY;
    return Math.hypot(dx, dy);
  }

  /**
   * Zoom-surface top-left in scroll-center **content** coordinates (layout only, no transform).
   * Used so pinch/wheel focal math matches `translate(pan) scale(s)` with `transform-origin: 0 0`.
   */
  private layoutZoomSurfaceInScrollContent(): { x: number; y: number } | null {
    const sc = this.scrollCenterEl?.nativeElement;
    const surf = this.zoomSurfaceEl?.nativeElement;
    if (!sc || !surf) {
      return null;
    }
    let el: HTMLElement | null = surf;
    let x = 0;
    let y = 0;
    while (el && el !== sc) {
      x += el.offsetLeft;
      y += el.offsetTop;
      const parent = el.offsetParent as HTMLElement | null;
      if (!parent) {
        if (surf.offsetParent === sc) {
          return { x: surf.offsetLeft, y: surf.offsetTop };
        }
        const scRect = sc.getBoundingClientRect();
        const sRect = surf.getBoundingClientRect();
        return {
          x: sRect.left - scRect.left + sc.scrollLeft,
          y: sRect.top - scRect.top + sc.scrollTop,
        };
      }
      el = parent;
    }
    if (el !== sc) {
      if (surf.offsetParent === sc) {
        return { x: surf.offsetLeft, y: surf.offsetTop };
      }
      const scRect = sc.getBoundingClientRect();
      const sRect = surf.getBoundingClientRect();
      return {
        x: sRect.left - scRect.left + sc.scrollLeft,
        y: sRect.top - scRect.top + sc.scrollTop,
      };
    }
    return { x, y };
  }

  private pinchFocalInSurfaceSpace(t1: Touch, t2: Touch): { fx: number; fy: number } | null {
    return this.focalPointInZoomSurfaceSpace(
      (t1.clientX + t2.clientX) / 2,
      (t1.clientY + t2.clientY) / 2
    );
  }

  private wheelFocalInSurfaceSpace(cx: number, cy: number): { fx: number; fy: number } | null {
    return this.focalPointInZoomSurfaceSpace(cx, cy);
  }

  /** Pinch / wheel focal in zoom-surface space (same basis as `translate(pan)` + live layout base). */
  private focalPointInZoomSurfaceSpace(cx: number, cy: number): { fx: number; fy: number } | null {
    const base = this.layoutZoomSurfaceInScrollContent();
    const sc = this.scrollCenterEl?.nativeElement;
    if (!base || !sc) {
      return null;
    }
    const scRect = sc.getBoundingClientRect();
    const contentX = cx - scRect.left + sc.scrollLeft;
    const contentY = cy - scRect.top + sc.scrollTop;
    return { fx: contentX - base.x, fy: contentY - base.y };
  }

  /** Keep the point `(fx, fy)` fixed while scale changes (surface-local, origin top-left). */
  private applyAlbumZoomFocalPan(
    prevScale: number,
    nextScale: number,
    fx: number,
    fy: number
  ): void {
    if (prevScale <= 0 || nextScale <= 0) {
      return;
    }
    const ratio = nextScale / prevScale;
    if (Math.abs(ratio - 1) < 1e-8) {
      return;
    }
    this.albumPanX = fx - (fx - this.albumPanX) * ratio;
    this.albumPanY = fy - (fy - this.albumPanY) * ratio;
  }

  /**
   * Keeps the scaled zoom surface pannable to every edge/corner. The old symmetric
   * ±(content−viewport)/2 clamp assumed pan 0 = centered, which is false for
   * `transform-origin: 0 0` + flex — it fought focal zoom and felt “locked” to the middle.
   */
  private clampAlbumPan(): void {
    const sc = this.scrollCenterEl?.nativeElement;
    const surf = this.zoomSurfaceEl?.nativeElement;
    if (!sc || !surf) {
      return;
    }
    const s = this.albumZoomScale;
    if (s <= V2_ALBUM_ZOOM_MIN) {
      this.albumPanX = 0;
      this.albumPanY = 0;
      return;
    }
    const base = this.layoutZoomSurfaceInScrollContent();
    if (!base) {
      return;
    }
    const sw = surf.offsetWidth * s;
    const sh = surf.offsetHeight * s;
    const cs = getComputedStyle(sc);
    const padL = parseFloat(cs.paddingLeft) || 0;
    const padR = parseFloat(cs.paddingRight) || 0;
    const padT = parseFloat(cs.paddingTop) || 0;
    const padB = parseFloat(cs.paddingBottom) || 0;

    const Vl = sc.scrollLeft + padL;
    const Vr = sc.scrollLeft + sc.clientWidth - padR;
    const Vt = sc.scrollTop + padT;
    const Vb = sc.scrollTop + sc.clientHeight - padB;

    const slack = 20;
    let minPx = Vr - sw - base.x - slack;
    let maxPx = Vl - base.x + slack;
    let minPy = Vb - sh - base.y - slack;
    let maxPy = Vt - base.y + slack;

    // When scaled content is smaller than the viewport on an axis, the “tight” bounds
    // invert (minPx > maxPx). A symmetric band around the midpoint felt locked to center;
    // use the full overlap range so the user can still pan the surface freely.
    if (minPx > maxPx) {
      minPx = Vl - sw - base.x - slack;
      maxPx = Vr - base.x + slack;
    }
    if (minPy > maxPy) {
      minPy = Vt - sh - base.y - slack;
      maxPy = Vb - base.y + slack;
    }

    this.albumPanX = Math.min(maxPx, Math.max(minPx, this.albumPanX));
    this.albumPanY = Math.min(maxPy, Math.max(minPy, this.albumPanY));
  }

  /**
   * While zoomed, the horizontal carousel gesture is disabled so one finger pans the page.
   * Restoring 1× re-enables the carousel.
   *
   * `resyncTrack`: only when leaving zoom (or explicit reset). Must not run on every
   * `touchend` at 1× — that snaps the track without animation and fights `snapBack` /
   * `snapToPage` after a swipe.
   */
  private syncAlbumZoomCarouselGate(options?: { resyncTrack?: boolean }): void {
    if (this.albumZoomEaseOutActive) {
      return;
    }
    const zoomed = this.albumZoomScale >= V2_ALBUM_ZOOM_REATTACH_THRESHOLD;
    if (zoomed) {
      if (this.carouselGesture) {
        this.destroyGesture();
      }
      this.cdr.markForCheck();
      return;
    }
    // Pinch/wheel can sit between 1× and the reattach threshold — keep carousel off without
    // snapping scale/pan back to 1× (that used to fight active pinch and felt like swipe+zoom).
    if (this.pinchActive || this.albumZoomScale > V2_ALBUM_ZOOM_MIN) {
      if (this.carouselGesture) {
        this.destroyGesture();
      }
      this.cdr.markForCheck();
      return;
    }
    this.albumZoomScale = V2_ALBUM_ZOOM_MIN;
    this.albumPanX = 0;
    this.albumPanY = 0;
    if (
      this.albumContentMounted &&
      !this.showAlbumLayoutLoading &&
      this.viewportEl?.nativeElement &&
      !this.carouselGesture
    ) {
      this.attachCarouselGesture();
    }
    if (options?.resyncTrack) {
      this.setTrackTransform(this.computeBaseTrackX(), false);
    }
    this.cdr.markForCheck();
  }

  /**
   * Tear down horizontal swipe and any in-progress drag the instant a pinch is recognized,
   * so the carousel doesn’t nudge the track before zoom crosses the reattach threshold.
   */
  private abortCarouselGestureForPinch(): void {
    this.clearSnapTimer();
    this.destroyGesture();
    this.isSnapping = false;
    this.viewportEl?.nativeElement.classList.remove('is-dragging');
    this.setTrackTransform(this.computeBaseTrackX(), false);
    this.slideInnerEls?.forEach(elRef => {
      const el = elRef.nativeElement;
      el.style.transition = '';
      el.style.transform = '';
      el.style.opacity = '';
    });
    this.cdr.markForCheck();
  }

  private attachAlbumZoomListeners(): void {
    this.detachAlbumZoomListeners();
    if (this.prefersReducedMotionAlbum()) {
      return;
    }
    const el = this.scrollCenterEl?.nativeElement;
    if (!el) {
      return;
    }
    const onStart = (e: TouchEvent) => this.onAlbumZoomTouchStart(e);
    const onMove = (e: TouchEvent) => this.onAlbumZoomTouchMove(e);
    const onEnd = (e: TouchEvent) => this.onAlbumZoomTouchEnd(e);
    const onWheel = (e: WheelEvent) => this.onAlbumZoomWheel(e);
    const capStart = { capture: true, passive: true };
    const capMove = { capture: true, passive: false };
    const capEnd = { capture: true };
    el.addEventListener('touchstart', onStart, capStart);
    el.addEventListener('touchmove', onMove, capMove);
    el.addEventListener('touchend', onEnd, capEnd);
    el.addEventListener('touchcancel', onEnd, capEnd);
    el.addEventListener('wheel', onWheel, { passive: false });
    this.albumZoomTouchCleanup = () => {
      el.removeEventListener('touchstart', onStart, capStart);
      el.removeEventListener('touchmove', onMove, capMove);
      el.removeEventListener('touchend', onEnd, capEnd);
      el.removeEventListener('touchcancel', onEnd, capEnd);
      el.removeEventListener('wheel', onWheel);
    };
  }

  private detachAlbumZoomListeners(): void {
    this.albumZoomTouchCleanup?.();
    this.albumZoomTouchCleanup = null;
  }

  private onAlbumZoomTouchStart(ev: TouchEvent): void {
    if (
      this.showAlbumLayoutLoading ||
      !this.domOpen ||
      this.isClosing ||
      this.prefersReducedMotionAlbum()
    ) {
      return;
    }
    this.cancelAlbumZoomResetAnimation();
    if (ev.touches.length === 2) {
      this.pinchActive = true;
      this.panActive = false;
      this.pinchStartDist = this.albumTouchDistance(ev.touches[0], ev.touches[1]);
      this.pinchStartScale = this.albumZoomScale;
      this.abortCarouselGestureForPinch();
      return;
    }
    if (ev.touches.length === 1 && this.albumZoomScale >= V2_ALBUM_ZOOM_REATTACH_THRESHOLD) {
      this.panActive = true;
      const t = ev.touches[0];
      this.panStartX = t.clientX;
      this.panStartY = t.clientY;
      this.panOriginX = this.albumPanX;
      this.panOriginY = this.albumPanY;
    }
  }

  private onAlbumZoomTouchMove(ev: TouchEvent): void {
    if (
      this.showAlbumLayoutLoading ||
      !this.domOpen ||
      this.isClosing ||
      this.prefersReducedMotionAlbum()
    ) {
      return;
    }
    if (ev.touches.length >= 2 && !this.pinchActive) {
      this.pinchActive = true;
      this.panActive = false;
      this.pinchStartDist = this.albumTouchDistance(ev.touches[0], ev.touches[1]);
      this.pinchStartScale = this.albumZoomScale;
      this.abortCarouselGestureForPinch();
    }
    if (this.pinchActive && ev.touches.length >= 2) {
      ev.preventDefault();
      const d = this.albumTouchDistance(ev.touches[0], ev.touches[1]);
      if (this.pinchStartDist > 6 && d > 0) {
        const prev = this.albumZoomScale;
        const next = Math.min(
          V2_ALBUM_ZOOM_MAX,
          Math.max(V2_ALBUM_ZOOM_MIN, this.pinchStartScale * (d / this.pinchStartDist))
        );
        const focal = this.pinchFocalInSurfaceSpace(ev.touches[0], ev.touches[1]);
        if (focal) {
          this.applyAlbumZoomFocalPan(prev, next, focal.fx, focal.fy);
        }
        this.albumZoomScale = next;
        this.clampAlbumPan();
        this.syncAlbumZoomCarouselGate();
      }
      this.cdr.markForCheck();
      return;
    }
    if (
      this.panActive &&
      ev.touches.length === 1 &&
      this.albumZoomScale >= V2_ALBUM_ZOOM_REATTACH_THRESHOLD
    ) {
      ev.preventDefault();
      const t = ev.touches[0];
      this.albumPanX = this.panOriginX + (t.clientX - this.panStartX);
      this.albumPanY = this.panOriginY + (t.clientY - this.panStartY);
      this.clampAlbumPan();
      this.cdr.markForCheck();
    }
  }

  private onAlbumZoomTouchEnd(ev: TouchEvent): void {
    const wasZoomed = this.albumZoomScale >= V2_ALBUM_ZOOM_REATTACH_THRESHOLD;
    if (ev.touches.length < 2) {
      this.pinchActive = false;
    }
    if (ev.touches.length === 0) {
      this.panActive = false;
    }
    if (ev.touches.length === 0 && wasZoomed && !this.prefersReducedMotionAlbum()) {
      this.animateAlbumZoomToNormal();
      return;
    }
    if (this.albumZoomScale < V2_ALBUM_ZOOM_REATTACH_THRESHOLD) {
      this.albumZoomScale = V2_ALBUM_ZOOM_MIN;
      this.albumPanX = 0;
      this.albumPanY = 0;
    } else {
      this.clampAlbumPan();
    }
    const nowZoomed = this.albumZoomScale >= V2_ALBUM_ZOOM_REATTACH_THRESHOLD;
    this.syncAlbumZoomCarouselGate({ resyncTrack: wasZoomed && !nowZoomed });
    this.cdr.markForCheck();
  }

  private onAlbumZoomWheel(ev: WheelEvent): void {
    if (
      this.showAlbumLayoutLoading ||
      !this.domOpen ||
      this.isClosing ||
      this.prefersReducedMotionAlbum()
    ) {
      return;
    }
    if (!ev.ctrlKey && !ev.metaKey) {
      return;
    }
    ev.preventDefault();
    this.cancelAlbumZoomResetAnimation();
    const wasZoomed = this.albumZoomScale >= V2_ALBUM_ZOOM_REATTACH_THRESHOLD;
    const prevScale = this.albumZoomScale;
    const delta = -ev.deltaY;
    const factor = 1 + Math.min(0.2, Math.max(-0.2, delta * 0.01));
    const next = Math.min(
      V2_ALBUM_ZOOM_MAX,
      Math.max(V2_ALBUM_ZOOM_MIN, this.albumZoomScale * factor)
    );
    if (next < V2_ALBUM_ZOOM_REATTACH_THRESHOLD) {
      this.albumZoomScale = V2_ALBUM_ZOOM_MIN;
      this.albumPanX = 0;
      this.albumPanY = 0;
    } else {
      const focal = this.wheelFocalInSurfaceSpace(ev.clientX, ev.clientY);
      if (focal) {
        this.applyAlbumZoomFocalPan(prevScale, next, focal.fx, focal.fy);
      }
      this.albumZoomScale = next;
      this.clampAlbumPan();
    }
    const nowZoomed = this.albumZoomScale >= V2_ALBUM_ZOOM_REATTACH_THRESHOLD;
    this.syncAlbumZoomCarouselGate({ resyncTrack: wasZoomed && !nowZoomed });
    this.cdr.markForCheck();
  }

  /* ——— Carousel: gesture setup ——— */

  private attachCarouselGesture(): void {
    this.destroyGesture();
    const el = this.viewportEl?.nativeElement;
    if (!el) return;
    this.measureSlideWidth();

    this.carouselGesture = this.gestureCtrl.create({
      el,
      gestureName: 'album-carousel',
      threshold: 10,
      onStart: () => {
        if (this.isSnapping) return;
        this.measureSlideWidth();
        this.viewportEl?.nativeElement.classList.add('is-dragging');
      },
      onMove: (ev: any) => {
        if (this.isSnapping) return;
        // Let vertical scroll win when the gesture is predominantly vertical
        if (Math.abs(ev.deltaY) > Math.abs(ev.deltaX) * 1.5) return;
        this.updateDragDOM(ev.deltaX);
      },
      onEnd: (ev: any) => {
        if (this.isSnapping) return;

        const n = this.albumPagesSorted.length;
        const dist = ev.deltaX as number;
        const vel: number = ev.velocityX ?? 0;
        const W = this.slideWidthPx;
        const commitByDist = Math.abs(dist) > W * V2AlbumOverlayComponent.DISTANCE_THRESHOLD_RATIO;
        const commitByVel = Math.abs(vel) > V2AlbumOverlayComponent.VELOCITY_THRESHOLD;
        const shouldCommit = commitByDist || commitByVel;

        if (shouldCommit && dist > 0 && this.activePageIndex > 0) {
          this.snapToPage(this.activePageIndex - 1);
        } else if (shouldCommit && dist < 0 && this.activePageIndex < n - 1) {
          this.snapToPage(this.activePageIndex + 1);
        } else {
          this.isSnapping = true;
          this.snapBack();
        }
      },
    });
    this.carouselGesture.enable(true);
  }

  private destroyGesture(): void {
    this.carouselGesture?.destroy();
    this.carouselGesture = null;
  }

  /* ——— Album navigation ——— */

  prevPage(): void {
    if (this.activePageIndex > 0 && !this.isSnapping) {
      this.snapToPage(this.activePageIndex - 1);
    }
  }

  nextPage(): void {
    if (this.activePageIndex < this.albumPagesSorted.length - 1 && !this.isSnapping) {
      this.snapToPage(this.activePageIndex + 1);
    }
  }

  goToPage(index: number): void {
    if (
      index < 0 ||
      index >= this.albumPagesSorted.length ||
      index === this.activePageIndex ||
      this.isSnapping
    ) {
      return;
    }
    // Animate only when close enough that the track motion looks intentional.
    // For far jumps (e.g. dot-tapping from page 1 to page 28) commit instantly.
    const isNear = Math.abs(index - this.activePageIndex) <= 2;
    if (isNear && this.uiAlbumPageIndices.includes(index)) {
      this.snapToPage(index);
    } else {
      this.commitToIndex(index, { showSkeleton: true });
    }
  }

  /* ——— Initialization on open ——— */

  private initAlbumState(): void {
    if (!this.albumPagesSorted?.length) {
      this.albumContentMounted = false;
      this.cancelAlbumZoomResetAnimation();
      this.detachAlbumZoomListeners();
      this.albumZoomScale = V2_ALBUM_ZOOM_MIN;
      this.albumPanX = 0;
      this.albumPanY = 0;
      this.pinchActive = false;
      this.panActive = false;
      this.pageIndexList = [];
      this.uiAlbumPageIndices = [];
      this.draggableLayoutByPageIndex = {};
      this.insertedStickerNaturalByItemId.clear();
      this.albumSkeletonVisible = false;
      return;
    }

    this.cancelAlbumZoomResetAnimation();
    this.albumContentMounted = true;

    this.rebuildStickerIconPathMap();

    this.albumSkeletonVisible = true;
    this.albumImageWatchDomRetryCount = 0;
    this.syncPageIndexList();
    this.activePageIndex = this.initialPageIndex ?? 0;

    if (this.focusItemId) {
      const focused = this.findPageIndexForItem(String(this.focusItemId));
      if (focused >= 0) {
        this.activePageIndex = focused;
      }
    }

    this.activePage = this.albumPagesSorted[this.activePageIndex]?.pageDef ?? {};
    this.syncAlbumPageLayers();

    setTimeout(() => {
      this.syncAlbumPageLayers();
      this.prepareMountedCardPages();
      this.cdr.markForCheck();
      this.scrollActiveNavIntoView();
      this.schedulePrefetchAlbumWindow(this.activePageIndex);
      this.attachCarouselGesture();
      this.attachAlbumZoomListeners();
      // Position track after Angular writes the *ngFor slide list to the DOM.
      afterNextRender(() => {
        this.measureSlideWidth();
        this.setTrackTransform(this.computeBaseTrackX(), false);
        this.syncAlbumSkeletonWithActivePageImages();
        this.attachResizeObserver();
        // Move focus into overlay so keyboard nav works from first keypress
        this.overlayRootEl?.nativeElement.focus();
      }, { injector: this.injector });
    }, 0);
    this.scheduleBuyAlbumPeekReveal();
  }

  private clearBuyAlbumPeekTimer(): void {
    if (this.buyAlbumPeekTimer) {
      clearTimeout(this.buyAlbumPeekTimer);
      this.buyAlbumPeekTimer = null;
    }
  }

  /** Buy CTA slides up from behind the frosted nav pill after a short beat. */
  private scheduleBuyAlbumPeekReveal(): void {
    this.clearBuyAlbumPeekTimer();
    this.buyAlbumPeekVisible = false;
    if (!this.showBuyAlbumButton || !this.albumPagesSorted?.length) {
      return;
    }
    this.buyAlbumPeekTimer = setTimeout(() => {
      this.buyAlbumPeekTimer = null;
      this.buyAlbumPeekVisible = true;
      this.cdr.markForCheck();
    }, V2AlbumOverlayComponent.BUY_ALBUM_PEEK_DELAY_MS);
  }

  /**
   * Album page index that contains this sticker (grid from `albumSections`, then CARD/INSERT rows,
   * then DRAGGABLE `cards`). Returns -1 if not found so `initialPageIndex` stays in effect.
   */
  private findPageIndexForItem(itemId: string): number {
    const want = String(itemId);

    for (let i = 0; i < this.albumSections.length; i++) {
      const section = this.albumSections[i];
      if (!section) continue;
      for (const row of section) {
        if (!row) continue;
        for (const id of row) {
          if (id != null && String(id) === want) {
            return i;
          }
        }
      }
    }

    const n = this.albumPagesSorted?.length ?? 0;
    for (let i = 0; i < n; i++) {
      const pd = this.albumPagesSorted[i]?.pageDef;
      const name = pd?.page?.name as string | undefined;
      if (!name) continue;

      if (name === 'DRAGGABLE_PAGE' && Array.isArray(pd.cards)) {
        for (const c of pd.cards) {
          if (c?.item_id != null && String(c.item_id) === want) {
            return i;
          }
        }
      }

      if (name === 'INSERT_PAGE' || name === 'CARD_PAGE') {
        const rows = pd.newCardRows;
        if (!Array.isArray(rows)) continue;
        for (const row of rows) {
          if (!row?.item) continue;
          for (const cell of row.item) {
            if (
              cell &&
              typeof cell === 'object' &&
              cell.item_id != null &&
              String(cell.item_id) === want
            ) {
              return i;
            }
          }
        }
      }
    }

    return -1;
  }

  /**
   * Normalizes INSERT/CARD `newCardRows` in place (null removal, `auto` → numeric col
   * sizes, `l2` → `l`). Ionic `ion-col` only emits the grid flex/width calcs when `size`
   * is a concrete column count; leaving `auto` on neighbor slides caused the first sticker
   * row to collapse until that page became active and was normalized here.
   *
   * Backend payloads occasionally omit {@link row.item} arrays or send a single orientation
   * string (e.g. `"l"`/`"L"`) instead of per-column arrays. That broke `prepareCardPageRows`
   * (`row.item.length`) and `*ngFor="let ori of row.orientation"` (NG02200: NgFor rejects
   * string collections).
   */
  private prepareCardPageRows(pageDef: any): void {
    if (!pageDef?.page || pageDef.page.name === 'DRAGGABLE_PAGE') return;
    const rows = pageDef.newCardRows;
    if (!Array.isArray(rows)) return;

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (!row || typeof row !== 'object') continue;

      let items = row.item;
      if (!Array.isArray(items)) {
        row.item = items == null ? [] : [items];
        items = row.item as any[];
      }

      for (let j = 0; j < items.length; ) {
        if (items[j] === null) {
          items.splice(j, 1);
        } else {
          j++;
        }
      }

      const colCount = items.length;
      if (colCount === 0) {
        row.orientation = [];
        row.colSize = [];
        continue;
      }

      row.orientation = this.alignCardRowOrientationCells(row.orientation, colCount);

      let colSizes: any[] | undefined = row.colSize;
      if (!Array.isArray(colSizes)) colSizes = [];
      colSizes = colSizes.slice();
      while (colSizes.length < colCount) {
        colSizes.push('auto');
      }
      if (colSizes.length > colCount) colSizes.length = colCount;
      row.colSize = colSizes;

      for (let cs = 0; cs < colCount; cs++) {
        if (row.colSize[cs] === 'auto' && colCount === 4) {
          row.colSize[cs] = 3;
        }
        if (row.orientation[cs] === 'l2') {
          row.orientation[cs] = 'l';
        }
      }
    }
  }

  /**
   * One string value must become one entry per sticker column — never pass a bare string into
   * `*ngFor` (Angular differ NG02200).
   */
  private alignCardRowOrientationCells(raw: unknown, columnCount: number): string[] {
    const cell = (s: unknown): string => {
      const t = String(s ?? '').trim().toLowerCase();
      if (t === 'l2' || t === 'l' || t === 'p') return t;
      if (t === 'landscape') return 'l';
      if (t === 'portrait') return 'p';
      return 'p';
    };

    const n = Math.max(columnCount, 1);

    if (typeof raw === 'string') {
      const c = cell(raw.trim());
      return Array.from({ length: n }, () => c);
    }

    if (!Array.isArray(raw) || raw.length === 0) {
      return Array.from({ length: n }, () => 'p');
    }

    const out: string[] = [];
    for (let i = 0; i < n; i++) {
      out.push(cell(raw[i] ?? raw[raw.length - 1]));
    }
    return out;
  }

  /** Runs {@link prepareCardPageRows} for every album page currently mounted in the carousel. */
  private prepareMountedCardPages(): void {
    for (const idx of this.uiAlbumPageIndices) {
      this.prepareCardPageRows(this.albumPagesSorted[idx]?.pageDef);
    }
  }

  private refreshUiAlbumPageWindow(): void {
    const n = this.albumPagesSorted?.length ?? 0;
    if (n === 0) {
      this.uiAlbumPageIndices = [];
      return;
    }
    const r = V2AlbumOverlayComponent.NEIGHBOR_PAGE_RADIUS;
    const lo = Math.max(0, this.activePageIndex - r);
    const hi = Math.min(n - 1, this.activePageIndex + r);
    const next: number[] = [];
    for (let i = lo; i <= hi; i++) next.push(i);
    this.uiAlbumPageIndices = next;
  }

  private syncAlbumPageLayers(): void {
    this.refreshUiAlbumPageWindow();
    this.computeDraggableLayoutsForWindow();
  }

  private syncPageIndexList(): void {
    const n = this.totalPages;
    this.pageIndexList = n > 0 ? Array.from({ length: n }, (_, i) => i) : [];
  }

  /** Keeps the active segment dot visible inside the horizontal scroll strip. */
  private scrollActiveNavIntoView(): void {
    const run = (): void => {
      const list = this.navSegEls?.toArray();
      const el = list?.[this.activePageIndex]?.nativeElement;
      el?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    };
    requestAnimationFrame(() => requestAnimationFrame(run));
  }

  /* ——— Sticker URL resolution (same as user-collection-items grid: `getStickerUrlNew` + P/L placeholders) ——— */

  private rebuildStickerIconPathMap(): void {
    this.fullItemIconPathById.clear();
    this.fullItemPicturePathById.clear();
    for (const it of this.fullItemList) {
      if (it?.item_id == null) continue;
      const id = String(it.item_id);
      const path = String(it.icon_path ?? '').trim();
      if (path) this.fullItemIconPathById.set(id, path);
      const pic = it.picture_path;
      if (pic != null && String(pic).trim() !== '') {
        this.fullItemPicturePathById.set(id, String(pic).trim());
      }
    }
  }

  private picturePathIsInserted(value: unknown): boolean {
    if (value == null) return false;
    return String(value).trim().toLowerCase() === 'inserted';
  }

  /**
   * Partner “direct final image” draggable sticker (`picture_path === 'inserted'`).
   * Uses catalog {@link fullItemPicturePathById} when page JSON omits the flag.
   */
  isDraggableInsertedSticker(card: any): boolean {
    if (!card || card.item_id == null || card.isEmpty === true) return false;
    if (this.picturePathIsInserted(card.picture_path)) return true;
    return this.picturePathIsInserted(this.fullItemPicturePathById.get(String(card.item_id)));
  }

  /** `img` load: cache intrinsic size; prefer partner `insertedDisplayWidth/Height` from page JSON when present. */
  onInsertedDraggableImageLoad(
    pageIdx: number,
    cardIndex: number,
    itemId: string,
    ev: Event
  ): void {
    const img = ev.target as HTMLImageElement;
    const nw = img.naturalWidth || 0;
    const nh = img.naturalHeight || 0;
    if (nw <= 0 || nh <= 0) return;
    const id = String(itemId ?? '');
    if (id) this.insertedStickerNaturalByItemId.set(id, { nw, nh });

    const layout = this.draggableLayoutByPageIndex[pageIdx];
    if (!layout?.draggableCards?.[cardIndex]) return;

    const sx = layout.width / V2AlbumOverlayComponent.PARTNER_DRAGGABLE_PAGE_WIDTH;
    const sy = layout.height / V2AlbumOverlayComponent.PARTNER_DRAGGABLE_PAGE_HEIGHT;
    const card = this.albumPagesSorted[pageIdx]?.pageDef?.cards?.[cardIndex];
    if (
      this.applyInsertedStickerFromStoredDisplaySize(
        layout.draggableCards[cardIndex],
        sx,
        sy,
        card,
        card?.orientation
      )
    ) {
      this.cdr.markForCheck();
      return;
    }
    this.applyInsertedStickerIntrinsicToRow(
      layout.draggableCards[cardIndex],
      sx,
      sy,
      id,
      card?.orientation
    );
    this.cdr.markForCheck();
  }

  /** Partner page JSON: layout size in partner px for direct-upload stickers. */
  private getInsertedDisplaySizePartnerPx(card: any): { w: number; h: number } | null {
    if (!card) return null;
    const w = Number(card.insertedDisplayWidth ?? card.inserted_display_width);
    const h = Number(card.insertedDisplayHeight ?? card.inserted_display_height);
    if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
    return { w, h };
  }

  /**
   * Scale partner-stored display size to the overlay album; clamp to P/L slot (same cap as intrinsic path).
   */
  private applyInsertedStickerFromStoredDisplaySize(
    row: any,
    scalingFactorX: number,
    scalingFactorY: number,
    card: any,
    slotOrientation?: string | null
  ): boolean {
    const sz = this.getInsertedDisplaySizePartnerPx(card);
    if (!sz) return false;
    const isL = String(slotOrientation ?? card?.orientation ?? 'P').toUpperCase() === 'L';
    const maxW = (isL ? 216 : 162) * scalingFactorX;
    const maxH = (isL ? 162 : 216) * scalingFactorY;
    let rw = sz.w * scalingFactorX;
    let rh = sz.h * scalingFactorY;
    const scaleDown = Math.min(1, maxW / rw, maxH / rh);
    row.insertedRenderW = Math.round(rw * scaleDown * 100) / 100;
    row.insertedRenderH = Math.round(rh * scaleDown * 100) / 100;
    return true;
  }

  /**
   * Fit intrinsic bitmap inside the same P/L frame as trading-card slots (162×216 / 216×162 partner px).
   */
  private applyInsertedStickerIntrinsicToRow(
    row: any,
    scalingFactorX: number,
    scalingFactorY: number,
    itemId: string | null | undefined,
    slotOrientation?: string | null
  ): void {
    const id = itemId != null && itemId !== '' ? String(itemId) : '';
    if (!id) return;
    const nat = this.insertedStickerNaturalByItemId.get(id);
    if (!nat || nat.nw <= 0 || nat.nh <= 0) return;
    const isL = String(slotOrientation ?? 'P').toUpperCase() === 'L';
    const maxW = (isL ? 216 : 162) * scalingFactorX;
    const maxH = (isL ? 162 : 216) * scalingFactorY;
    const scale = Math.min(maxW / nat.nw, maxH / nat.nh, 1);
    row.insertedRenderW = Math.round(nat.nw * scale * 100) / 100;
    row.insertedRenderH = Math.round(nat.nh * scale * 100) / 100;
  }

  /**
   * Mirrors items grid `[src]="item.icon_path ? getStickerUrlNew(item) : …noStickerImgL/P"`:
   * `icon_location_prefix + '/' + icon_path`, else placeholder by slot orientation.
   */
  getStickerFunction(itemId: string, orientation: string): string {
    const id = String(itemId);
    const sub = this.fullItemIconPathById.get(id)?.trim();
    if (sub) {
      return this.joinIconLocationPath(this.iconLocationPrefix, sub);
    }
    const prefix = (this.iconLocationPrefix ?? '').replace(/\/+$/, '');
    const useP = String(orientation).toUpperCase() === 'P';
    const file = useP ? this.noStickerImgP : this.noStickerImgL;
    if (!file) return '';
    return `${prefix}/${file}`;
  }

  /** True when the user owns the sticker (`ownedItemIds` / Tenho). */
  stickerOwned(itemId: string): boolean {
    return this.ownedItemIds.has(String(itemId));
  }

  /** Single call for template: avoids repeating `getStickerFunction` in the view. */
  getAlbumStickerSlot(itemId: string, orientation: string): { src: string; owned: boolean } {
    const id = String(itemId);
    return {
      src: this.getStickerFunction(id, orientation),
      owned: this.stickerOwned(id),
    };
  }

  private joinIconLocationPath(prefix: string, iconPath: string): string {
    const p = (prefix ?? '').replace(/\/+$/, '');
    const sub = (iconPath ?? '').replace(/^\/+/, '');
    if (!sub) return '';
    return p ? `${p}/${sub}` : `/${sub}`;
  }

  /* ——— Neighbor page image prefetch ——— */

  private normalizeAlbumBackgroundUrl(bg: unknown): string | null {
    const s = this.albumBackgroundImageSrc(bg);
    return s.length > 0 ? s : null;
  }

  private addPrefetchCandidate(out: Set<string>, url: unknown): void {
    if (url == null || typeof url !== 'string') return;
    const t = url.trim();
    if (t.length < 4) return;
    out.add(t);
  }

  private collectImageUrlsFromPageDef(pageDef: any, out: Set<string>): void {
    if (!pageDef?.page?.name) return;
    const name = pageDef.page.name as string;
    this.addPrefetchCandidate(out, this.normalizeAlbumBackgroundUrl(pageDef.background));

    if (name === 'INSERT_PAGE' || name === 'CARD_PAGE') {
      this.addPrefetchCandidate(out, pageDef.leftLogo);
      this.addPrefetchCandidate(out, pageDef.rightLogo);
      const rows = pageDef.newCardRows;
      if (!Array.isArray(rows)) return;
      for (const row of rows) {
        if (!row?.orientation || !Array.isArray(row.orientation)) continue;
        for (let ci = 0; ci < row.orientation.length; ci++) {
          const ori = row.orientation[ci];
          const item = row.item?.[ci];
          if (item === 'custom_image') {
            this.addPrefetchCandidate(out, row.custom_image?.[ci]);
            continue;
          }
          if (item == null || item === 'empty_spot' || typeof item !== 'object' || item.item_id == null) {
            continue;
          }
          const orient = ori === 'l' || ori === 'l2' ? 'L' : ori === 'p' ? 'P' : 'L';
          this.addPrefetchCandidate(out, this.getStickerFunction(String(item.item_id), orient));
        }
      }
      return;
    }

    if (name === 'DRAGGABLE_PAGE') {
      if (Array.isArray(pageDef.cards)) {
        for (const c of pageDef.cards) {
          if (c?.item_id != null) {
            const o = String(c.orientation ?? 'P').toUpperCase();
            this.addPrefetchCandidate(out, this.getStickerFunction(String(c.item_id), o === 'L' ? 'L' : 'P'));
          }
        }
      }
      if (Array.isArray(pageDef.draggableLogos)) {
        for (const l of pageDef.draggableLogos) this.addPrefetchCandidate(out, l?.src);
      }
      if (Array.isArray(pageDef.draggableImg)) {
        for (const d of pageDef.draggableImg) this.addPrefetchCandidate(out, d?.imageSrc);
      }
    }
  }

  private prefetchAlbumWindowAround(centerIndex: number): void {
    const n = this.albumPagesSorted?.length ?? 0;
    if (n === 0) return;
    const r = V2AlbumOverlayComponent.NEIGHBOR_PAGE_RADIUS;
    const lo = Math.max(0, centerIndex - r);
    const hi = Math.min(n - 1, centerIndex + r);
    const urls = new Set<string>();
    for (let i = lo; i <= hi; i++) {
      const pd = this.albumPagesSorted[i]?.pageDef;
      if (pd) this.collectImageUrlsFromPageDef(pd, urls);
    }
    let stagger = 0;
    for (const u of urls) {
      if (this.prefetchedImageUrls.has(u)) continue;
      this.prefetchedImageUrls.add(u);
      const href = u;
      setTimeout(() => {
        const img = new Image();
        img.decoding = 'async';
        img.src = href;
      }, stagger);
      stagger += 6;
    }
  }

  private schedulePrefetchAlbumWindow(centerIndex: number): void {
    const run = (): void => this.prefetchAlbumWindowAround(centerIndex);
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(() => run(), { timeout: 2500 });
    } else {
      setTimeout(run, 120);
    }
  }

  /* ——— Draggable page geometry ——— */

  private buildDraggableLayout(pageDef: any, albumWidth: number, albumHeight: number): any {
    if (!pageDef || pageDef.page?.name !== 'DRAGGABLE_PAGE') return null;

    const partnerWidth = V2AlbumOverlayComponent.PARTNER_DRAGGABLE_PAGE_WIDTH;
    const partnerHeight = V2AlbumOverlayComponent.PARTNER_DRAGGABLE_PAGE_HEIGHT;
    const partnerCardWidthPortrait = 162;
    const partnerCardHeightPortrait = 216;
    const partnerCardWidthLandscape = 216;
    const partnerCardHeightLandscape = 162;
    const partnerLogoWidth = 115;
    const partnerLogoHeight = 110;

    const scalingFactorX = albumWidth / partnerWidth;
    const scalingFactorY = albumHeight / partnerHeight;

    const layout: any = {
      width: albumWidth,
      height: albumHeight,
      draggableCardWidthPortrait: partnerCardWidthPortrait * scalingFactorX,
      draggableCardHeightPortrait: partnerCardHeightPortrait * scalingFactorY,
      draggableCardWidthLandscape: partnerCardWidthLandscape * scalingFactorX,
      draggableCardHeightLandscape: partnerCardHeightLandscape * scalingFactorY,
      draggableCards: [],
      draggableLogoWidth: partnerLogoWidth * scalingFactorX,
      draggableLogoHeight: partnerLogoHeight * scalingFactorY,
      draggableLogos: [],
      draggableHeader: undefined as any,
      draggableFooter: undefined as any,
      draggableTexts: [] as any[],
      draggableTextWidth: [] as number[],
      draggableTextHeight: [] as number[],
      draggableImgs: [] as any[],
    };

    if (pageDef.cards !== undefined) {
      layout.draggableCards = pageDef.cards.map((card: any) => {
        const row: any = {
          scaledMarginLeft: (parseFloat(card.stickerMarginLeft) * scalingFactorX).toFixed(2) + 'px',
          scaledMarginTop: (parseFloat(card.stickerMarginTop) * scalingFactorY).toFixed(2) + 'px',
        };
        if (this.isDraggableInsertedSticker(card)) {
          const isL = String(card.orientation ?? 'P').toUpperCase() === 'L';
          row.insertedPendingMaxW = (isL ? 216 : 162) * scalingFactorX;
          row.insertedPendingMaxH = (isL ? 162 : 216) * scalingFactorY;
          if (
            !this.applyInsertedStickerFromStoredDisplaySize(
              row,
              scalingFactorX,
              scalingFactorY,
              card,
              card.orientation
            )
          ) {
            this.applyInsertedStickerIntrinsicToRow(
              row,
              scalingFactorX,
              scalingFactorY,
              card.item_id,
              card.orientation
            );
          }
        }
        return row;
      });
    }

    if (pageDef.draggableLogos !== undefined) {
      layout.draggableLogos = pageDef.draggableLogos.map((logo: any) => ({
        scaledLeft: (parseFloat(logo.left) * scalingFactorX).toFixed(2) + 'px',
        scaledTop: (parseFloat(logo.top) * scalingFactorY).toFixed(2) + 'px',
      }));
    }

    const scaledHeaderLeft = parseFloat(pageDef.sectionHeader?.draggableTitleLeft ?? '0') * scalingFactorX;
    const scaledHeaderTop = parseFloat(pageDef.sectionHeader?.draggableTitleTop ?? '0') * scalingFactorY;
    const scaledHeaderFontSize = parseFloat(pageDef.sectionHeaderFontSize ?? '0') * scalingFactorY * 0.9;
    layout.draggableHeader = {
      scaledLeft: scaledHeaderLeft.toFixed(2) + 'px',
      scaledTop: scaledHeaderTop.toFixed(2) + 'px',
      scaledFontSize: scaledHeaderFontSize.toFixed(2) + 'px',
    };

    const scaledFooterLeft = parseFloat(pageDef.sectionFooter?.draggableFotterLeft ?? '0') * scalingFactorX;
    const scaledFooterTop = parseFloat(pageDef.sectionFooter?.draggableFotterTop ?? '0') * scalingFactorY;
    const scaledFooterFontSize = parseFloat(pageDef.sectionFooterFontSize ?? '0') * scalingFactorY * 0.9;
    layout.draggableFooter = {
      scaledLeft: scaledFooterLeft.toFixed(2) + 'px',
      scaledTop: scaledFooterTop.toFixed(2) + 'px',
      scaledFontSize: scaledFooterFontSize.toFixed(2) + 'px',
    };

    if (pageDef.draggableText !== undefined) {
      for (let i = 0; i < pageDef.draggableText.length; i++) {
        const t = pageDef.draggableText[i];
        layout.draggableTextWidth[i] = t.width * scalingFactorX;
        layout.draggableTextHeight[i] = t.height * scalingFactorY;
        const scaledLeft = parseFloat(t.left) * scalingFactorX;
        const scaledTop = parseFloat(t.top) * scalingFactorY;
        const scaledPadding = 20 * scalingFactorX;
        layout.draggableTexts.push({
          scaledLeft: scaledLeft.toFixed(2) + 'px',
          scaledTop: scaledTop.toFixed(2) + 'px',
          scaledBorderRadius: t.borderRadius * scalingFactorX + 'px',
          backgroundColor: t.backgroundColor,
          isBackgroundTransparent: t.isBackgroundTrasparent,
          scaledPadding: scaledPadding.toFixed(2) + 'px',
          scaledFontSizeH1: 2.1 * scalingFactorX,
          scaledFontSizeHuge: 2.4 * scalingFactorX,
          scalingFactor: scalingFactorX,
          text: t.text,
        });
      }
    }

    if (pageDef.draggableImg !== undefined) {
      layout.draggableImgs = pageDef.draggableImg.map((img: any) => ({
        scaledLeft: (parseFloat(img.left) * scalingFactorX).toFixed(2) + 'px',
        scaledTop: (parseFloat(img.top) * scalingFactorY).toFixed(2) + 'px',
        scaledBorderRadius: img.borderRadius * scalingFactorX + 'px',
        backgroundColor: img.backgroundColor,
        isBackgroundTransparent: img.isBackgroundTrasparent,
        width: img.width * scalingFactorX,
        height: img.height * scalingFactorY,
        imageSrc: img.imageSrc,
      }));
    }

    return layout;
  }

  private computeDraggableLayoutsForWindow(): void {
    for (const key of Object.keys(this.draggableLayoutByPageIndex)) {
      const k = Number(key);
      if (!this.uiAlbumPageIndices.includes(k)) {
        delete this.draggableLayoutByPageIndex[k];
      }
    }

    // Measure from the carousel viewport (unscaled slide size)
    const albumWidth = this.viewportEl?.nativeElement?.offsetWidth ?? 0;
    const albumHeight = this.viewportEl?.nativeElement?.offsetHeight ?? 0;

    this.width = albumWidth;
    this.height = albumHeight;

    if (albumWidth === 0 || albumHeight === 0) return;

    for (const idx of this.uiAlbumPageIndices) {
      const pd = this.albumPagesSorted[idx]?.pageDef;
      if (pd?.page?.name === 'DRAGGABLE_PAGE') {
        this.draggableLayoutByPageIndex[idx] = this.buildDraggableLayout(pd, albumWidth, albumHeight);
      } else {
        delete this.draggableLayoutByPageIndex[idx];
      }
    }
  }

  /* ——— Overlay lifecycle helpers ——— */

  private emitClosed(): void {
    this.closed.emit({ activePageIndex: this.activePageIndex });
  }

  private getOverlayCloseAnimDurationMs(): number {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return V2AlbumOverlayComponent.OVERLAY_CLOSE_ANIM_MS;
    }
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 0
      : V2AlbumOverlayComponent.OVERLAY_CLOSE_ANIM_MS;
  }

  private clearCloseAnimationFinishTimer(): void {
    if (this.closeAnimationFinishTimer) {
      clearTimeout(this.closeAnimationFinishTimer);
      this.closeAnimationFinishTimer = null;
    }
  }

  private clearSnapTimer(): void {
    if (this.snapTimer) {
      clearTimeout(this.snapTimer);
      this.snapTimer = null;
    }
  }

  private scheduleCloseAnimationFinish(): void {
    this.clearCloseAnimationFinishTimer();
    this.closeAnimationFinishTimer = setTimeout(() => {
      this.closeAnimationFinishTimer = null;
      this.isClosing = false;
      this.domOpen = false;
      this.setBodyScrollLock(false);
      this.cdr.markForCheck();
    }, this.getOverlayCloseAnimDurationMs());
  }

  /** Same pattern as v2-open-binders: restart CSS keyframe animation after reopening mid-close. */
  private resetOverlayEnterAnimationStyles(): void {
    const el = this.overlayRootEl?.nativeElement;
    if (!el) return;
    el.style.animation = 'none';
    el.style.opacity = '1';
    el.style.transform = 'scale(1)';
    void el.offsetWidth;
    el.style.removeProperty('animation');
    el.style.removeProperty('opacity');
    el.style.removeProperty('transform');
    this.resetAlbumCardEnterAnimationStyles();
  }

  /** Restarts the album card stack intro (mirrors v3-dc-pack-frame GSAP-from state via CSS). */
  private resetAlbumCardEnterAnimationStyles(): void {
    const el = this.albumCardBlockEl?.nativeElement;
    if (!el) return;
    el.style.animation = 'none';
    el.style.opacity = '1';
    el.style.transform = 'none';
    void el.offsetWidth;
    el.style.removeProperty('animation');
    el.style.removeProperty('opacity');
    el.style.removeProperty('transform');
  }

  private setBodyScrollLock(locked: boolean): void {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle('v2-album-overlay-open', locked);
  }
}
